import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "antd";
import BottomTabBar from "../components/BottomTabBar";
import StoreSwitcher from "../components/StoreSwitcher";
import { DEFAULT_DESIGN_OPTIONS } from "./MenuPreviewPanel";
import useAIMenu from "./useAIMenu";
import { loadCachedDesignOptions } from "../utils/menuDesignOptions";
import {
  itemHasVariations,
  formatItemCanvasPrice,
  validateVariationRows,
  rowsFromLegacyPrice,
} from "../utils/menuItem";
import {
  Star,
  RefreshCw,
  Eye,
  Plus,
  ChevronRight,
  Pencil,
  Trash2,
  X,
  Save,
  Upload,
  Copy,
  Heart,
  ImagePlus,
  Loader2,
  Sparkles,
} from "lucide-react";
import { generateItemImage, enhanceItemImage } from "../components/workflow/imageCreationService";
import ItemMediaUpload, { isVideoUrl } from "../components/ItemMediaUpload";

const SECTION_COLORS = [
  "bg-gray-900",
  "bg-yellow-400",
  "bg-green-200",
  "bg-blue-400",
  "bg-purple-400",
  "bg-red-400",
  "bg-orange-400",
  "bg-teal-400",
];

const AIMenuScreen = () => {
  const navigate = useNavigate();
  const {
    sections,
    selectedSectionId,
    setSelectedSectionId,
    isSectionModalOpen,
    setIsSectionModalOpen,
    editingSectionId,
    setEditingSectionId,
    isItemModalOpen,
    setIsItemModalOpen,
    isJsonPreviewOpen,
    setIsJsonPreviewOpen,
    isLoading,
    isRefreshing,
    isSyncing,
    editingItem,
    setEditingItem,
    customerId,
    designOptions,
    setDesignOptions,
    previewMode,
    setPreviewMode,
    currentSection,
    visibleSectionsForPreview,
    previewJson,
    previewCsv,
    fetchCustomerMenu,
    handleSave,
    handleSectionSubmit,
    handleDeleteSection,
    handleDuplicateSection,
    handleItemSubmit,
    handleDeleteItem,
    handleToggleItemVisibility,
    handleToggleItemFavourite,
    handleSetItemImage,
    handleUploadItemMedia,
    formatPrice,
    extractPriceHeading,
    navigateToExtractData,
    setSections,
  } = useAIMenu();

  // ── UI-only local state ──────────────────────────────────────────────────────
  const [view, setView] = useState("sections"); // "sections" | "items"
  const [itemFilter, setItemFilter] = useState("all");

  const [sectionNameInput, setSectionNameInput] = useState("");
  const [itemFormData, setItemFormData] = useState({
    name: "",
    price: "",
    priceHeading: "",
    description: "",
    variations: [], // editor rows [{ label, price }]; 0 rows = plain single price
  });
  const [variationErrors, setVariationErrors] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // ── AI item-image generation (Generate Image button on each card) ──────────────
  const [generatingImageForId, setGeneratingImageForId] = useState(null);
  const [enhancingImageForId, setEnhancingImageForId] = useState(null);
  const [pendingImage, setPendingImage] = useState(null); // { itemId, url, enhanced? }
  const [imageGenError, setImageGenError] = useState(null); // { itemId, message }
  const [previewImage, setPreviewImage] = useState(null); // { itemId, itemName, url, isPending }
  const [uploadingMediaForId, setUploadingMediaForId] = useState(null);

  // Photo / video from the device or camera (upload button on each card).
  const handleUploadMedia = async (item, file) => {
    setImageGenError(null);
    setPendingImage((p) => (p && p.itemId === item.id ? null : p)); // an unsaved generated preview would be replaced
    setUploadingMediaForId(item.id);
    const errorMessage = await handleUploadItemMedia(item.id, file);
    setUploadingMediaForId(null);
    if (errorMessage) setImageGenError({ itemId: item.id, message: errorMessage });
  };

  const handleGenerateItemImage = async (item) => {
    setImageGenError(null);
    setGeneratingImageForId(item.id);
    try {
      const data = await generateItemImage({ itemId: item.id, itemName: item.name });
      setPendingImage({ itemId: item.id, url: data.url });
    } catch (err) {
      setImageGenError({
        itemId: item.id,
        message: err?.message || "Couldn't generate the image. Please try again.",
      });
    } finally {
      setGeneratingImageForId(null);
    }
  };

  // "Enhance" works on the item's saved image (uploaded or generated): AI retouch -- same product, clean
  // white background, better light and sharpness. The result is an unsaved preview, exactly like a
  // generated image -- the saved image is only replaced when the merchant clicks Save. If the AI is
  // unavailable the server returns the standard (non-AI) enhance and flags it with `fallback`.
  const canEnhanceImage = (url) => !!url && !isVideoUrl(url) && !/\.gif(?:[?#].*)?$/i.test(String(url));

  const handleEnhanceItemImage = async (item) => {
    setImageGenError(null);
    setEnhancingImageForId(item.id);
    try {
      const data = await enhanceItemImage({ itemId: item.id });
      setPendingImage({ itemId: item.id, url: data.url, enhanced: true, fallback: !!data.fallback });
    } catch (err) {
      setImageGenError({
        itemId: item.id,
        message: err?.message || "Couldn't enhance the image. Please try again.",
      });
    } finally {
      setEnhancingImageForId(null);
    }
  };

  const handleSaveGeneratedImage = (itemId) => {
    if (!pendingImage || pendingImage.itemId !== itemId) return;
    handleSetItemImage(itemId, pendingImage.url);
    setPendingImage(null);
  };

  const handleDiscardGeneratedImage = () => {
    setPendingImage(null);
  };

  // Asks for confirmation first: removing deletes the saved image from the
  // database and S3, and it can't be undone (only regenerated).
  const handleRemoveSavedImage = (itemId, onRemoved) => {
    Modal.confirm({
      title: "Remove this image?",
      content: "The saved image or video will be permanently deleted. This can't be undone.",
      okText: "Remove",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        await handleSetItemImage(itemId, null);
        if (onRemoved) onRemoved();
      },
    });
  };

  const handleRegenerateFromPreview = () => {
    if (!previewImage) return;
    const item = filteredItems.find((i) => i.id === previewImage.itemId) || {
      id: previewImage.itemId,
      name: previewImage.itemName,
    };
    setPreviewImage(null);
    handleGenerateItemImage(item);
  };

  // ── Form submit wrappers ─────────────────────────────────────────────────────
  const handleSectionSubmitLocal = () => {
    if (!sectionNameInput.trim()) return;
    handleSectionSubmit({ name: sectionNameInput });
    setSectionNameInput("");
  };

  const handleItemSubmitLocal = () => {
    if (!itemFormData.name.trim()) return;
    if (itemFormData.variations.length > 0) {
      const { ok, errors } = validateVariationRows(itemFormData.variations);
      setVariationErrors(errors);
      if (!ok) return;
    }
    // One remaining variation is saved as a plain price: don't carry the old
    // combined heading ("Roll/Hero") over.
    const saved = handleItemSubmit(
      itemFormData.variations.length === 1 ? { ...itemFormData, priceHeading: "" } : itemFormData,
    );
    if (saved === false) return;
    setVariationErrors([]);
    setItemFormData({ name: "", price: "", priceHeading: "", description: "", variations: [] });
  };

  // ── Open helpers ─────────────────────────────────────────────────────────────
  const openAddSection = () => {
    setSectionNameInput("");
    setEditingSectionId(null);
    setIsSectionModalOpen(true);
  };

  const openEditSection = (e, s) => {
    e.stopPropagation();
    setSectionNameInput(s.name);
    setEditingSectionId(s.id);
    setIsSectionModalOpen(true);
  };

  const openAddItem = () => {
    setEditingItem(null);
    setVariationErrors([]);
    setItemFormData({ name: "", price: "", priceHeading: "", description: "", variations: [] });
    setIsItemModalOpen(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setVariationErrors([]);
    setItemFormData({
      name: item.name || "",
      price: item.price || "",
      priceHeading: itemHasVariations(item)
        ? item.variations.map((variation) => variation.label).join("/")
        : extractPriceHeading(item) || "",
      description: item.description || "",
      // Rows come from the structured variations, never from splitting the
      // combined price / heading strings.
      // Older items that lost their variations array (price "9.99/11.99",
      // heading "Roll/Hero") open as rows too, so one Save restores them.
      variations: itemHasVariations(item)
        ? item.variations.map((v) => ({ label: v.unnamed ? "" : v.label ?? "", price: String(v.price ?? "") }))
        : rowsFromLegacyPrice(item.price, extractPriceHeading(item) || item.priceHeading),
    });
    setIsItemModalOpen(true);
  };

  const handleSectionClick = (sectionId) => {
    setSelectedSectionId(sectionId);
    setView("items");
    setItemFilter("all");
  };

  const filteredItems = (currentSection?.items || []).filter((item) => {
    if (itemFilter === "visible") return item.isVisible !== false;
    if (itemFilter === "hidden") return item.isVisible === false;
    return true;
  });

  const designOptionsForPreview = () => {
    const cached = customerId ? loadCachedDesignOptions(customerId) : null;
    return {
      ...DEFAULT_DESIGN_OPTIONS,
      ...(cached || {}),
      ...designOptions,
    };
  };

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === "item") handleDeleteItem(deleteConfirm.id);
    if (deleteConfirm.type === "section") {
      handleDeleteSection(deleteConfirm.id);
      setView("sections");
    }
    setDeleteConfirm(null);
  };

  // ── Shared input class ───────────────────────────────────────────────────────
  const inputCls =
    "w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  // ════════════════════════════════════════════════════════════════════════════
  // SCREEN 1 — Section List
  // ════════════════════════════════════════════════════════════════════════════
  const SectionsView = () => (
    <div className="px-4 sm:px-5 pt-6 sm:pt-8 pb-4 max-w-2xl mx-auto w-full">
      {/* Header */}
      <h1 className="text-xl sm:text-3xl font-extrabold text-gray-900 leading-tight">
        AI Menu Creation
      </h1>
      <p className="text-xs sm:text-sm text-gray-500 mt-1">
        Design a simple restaurant menu for your TV screens.
      </p>

      {/* Action buttons */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() =>
            navigate("/menu-preview", {
              state: {
                sections,
                selectedSectionId,
                customerId,
                designOptions: designOptionsForPreview(),
              },
            })
          }
          className="flex items-center gap-1.5 bg-blue-600 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-2.5 rounded-full whitespace-nowrap"
        >
          <Star size={13} /> Generate Menu
        </button>
        <button
          type="button"
          onClick={() => fetchCustomerMenu(customerId)}
          disabled={isRefreshing || !customerId}
          className="flex items-center gap-1.5 border border-gray-300 text-gray-700 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2.5 rounded-full bg-white whitespace-nowrap disabled:opacity-50"
        >
          <RefreshCw
            size={13}
            className={
              isRefreshing
                ? "animate-spin motion-reduce:animate-none text-blue-600"
                : "text-gray-600"
            }
            style={isRefreshing ? { animationDuration: "0.85s" } : undefined}
          />
          {isRefreshing ? "Refreshing…" : "Refresh"}
        </button>
        <button
          onClick={navigateToExtractData}
          className="flex items-center gap-1.5 border border-gray-300 text-gray-700 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2.5 rounded-full bg-white whitespace-nowrap"
        >
          <Upload size={13} /> Extract Data
        </button>
        <button
          onClick={() => setIsJsonPreviewOpen(true)}
          disabled={!sections.length}
          className="flex items-center gap-1.5 border border-gray-300 text-gray-700 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2.5 rounded-full bg-white whitespace-nowrap disabled:opacity-50"
        >
          <Eye size={13} /> Preview
        </button>
      </div>

      {/* Sections header row */}
      <div className="mt-5 sm:mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm sm:text-base font-bold text-gray-900">Sections</span>
          <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {sections.length}
          </span>
        </div>
        <button
          onClick={openAddSection}
          className="bg-blue-600 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-2 rounded-full flex items-center gap-1"
        >
          <Plus size={13} /> Add Section
        </button>
      </div>

      {/* Section cards */}
      {sections.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-gray-400">
          <Star size={36} className="mb-3 opacity-30" />
          <p className="text-sm">No sections yet. Tap Add Section to start.</p>
        </div>
      ) : (
        <div className="space-y-3 mt-3">
          {sections.map((s, index) => (
            <div
              key={s.id}
              onClick={() => handleSectionClick(s.id)}
              className="bg-white rounded-2xl p-3 sm:p-4 flex items-center gap-3 shadow-sm border border-gray-100 cursor-pointer active:bg-gray-50"
            >
              {/* Icon box */}
              <div
                className={`${
                  SECTION_COLORS[index % SECTION_COLORS.length]
                } rounded-xl p-2 sm:p-2.5 flex-shrink-0`}
              >
                <Star size={14} className="text-white" />
              </div>

              {/* Name + count — min-w-0 so it can shrink */}
              <div className="min-w-0 flex-1">
                <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                  {s.name}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">
                  {(s.items || []).length} items
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={(e) => openEditSection(e, s)}
                  className="border border-gray-200 p-2 rounded-lg touch-manipulation"
                  aria-label="Edit section"
                >
                  <Pencil size={14} className="text-gray-400" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicateSection(s.id);
                  }}
                  className="border border-gray-200 p-2 rounded-lg touch-manipulation"
                  aria-label="Duplicate section"
                >
                  <Copy size={14} className="text-gray-400" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirm({ type: "section", id: s.id, name: s.name });
                  }}
                  className="bg-red-50 p-2 rounded-lg touch-manipulation"
                  aria-label="Delete section"
                >
                  <Trash2 size={14} className="text-red-400" />
                </button>
                <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB — mobile only */}
      <button
        onClick={openAddSection}
        className="sm:hidden fixed bottom-[5.5rem] right-4 bg-blue-600 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-30 touch-manipulation"
        aria-label="Add section"
      >
        <Plus size={22} className="text-white" />
      </button>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SCREEN 2 — Item List
  // ════════════════════════════════════════════════════════════════════════════
  const ItemsView = () => (
    <div className="max-w-2xl mx-auto w-full min-w-0">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white px-4 sm:px-5 pt-4 pb-3 shadow-sm border-b border-gray-100">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setView("sections")}
            className="text-xs sm:text-sm text-gray-600 border border-gray-200 px-2.5 sm:px-3 py-1.5 rounded-full flex-shrink-0 touch-manipulation"
          >
            ← Back
          </button>
          <h2 className="text-base sm:text-xl font-bold text-gray-900 truncate flex-1 min-w-0">
            {currentSection?.name}
          </h2>
          <button
            onClick={() =>
              navigate("/menu-preview", {
                state: {
                  sections,
                  selectedSectionId,
                  customerId,
                  designOptions: designOptionsForPreview(),
                },
              })
            }
            className="bg-blue-600 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full flex-shrink-0 touch-manipulation"
          >
            Next
          </button>
        </div>
      </div>

      {/* Filter chips + add on larger screens */}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-3 px-4 sm:px-5">
        <div className="flex flex-wrap gap-2">
        {["all", "visible", "hidden"].map((f) => (
          <button
            key={f}
            onClick={() => setItemFilter(f)}
            className={`text-xs sm:text-sm font-semibold px-3 sm:px-4 py-1.5 rounded-full capitalize ${
              itemFilter === f
                ? "bg-blue-600 text-white"
                : "border border-gray-300 text-gray-600 bg-white"
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        </div>
        <button
          onClick={openAddItem}
          className="hidden sm:flex items-center gap-1 bg-blue-600 text-white text-xs sm:text-sm font-bold px-3 py-1.5 rounded-full touch-manipulation"
        >
          <Plus size={13} /> Add Item
        </button>
      </div>

      {/* Item cards */}
      <div className="space-y-2 sm:space-y-3 px-4 sm:px-5 mt-3 pb-4">
        {filteredItems.length === 0 ? (
          <div className="mt-16 flex flex-col items-center text-gray-400">
            <p className="text-sm">No items found.</p>
          </div>
        ) : (
          filteredItems.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl px-3 sm:px-4 py-3 shadow-sm border border-gray-100"
            >
              <div className="flex items-start justify-between gap-2 min-w-0">
                {item.image && (
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() =>
                        setPreviewImage({
                          itemId: item.id,
                          itemName: item.name,
                          url: item.image,
                          isPending: false,
                        })
                      }
                      aria-label={`View image for ${item.name}`}
                      className="block touch-manipulation"
                    >
                      {isVideoUrl(item.image) ? (
                        <video
                          src={item.image}
                          muted
                          playsInline
                          preload="metadata"
                          className="w-10 h-10 rounded-lg object-cover border border-gray-100 bg-black"
                        />
                      ) : (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-10 h-10 rounded-lg object-cover border border-gray-100"
                        />
                      )}
                    </button>
                    <button
                      onClick={() => handleRemoveSavedImage(item.id)}
                      aria-label={`Remove image for ${item.name}`}
                      title="Remove image"
                      className="absolute -top-1.5 -right-1.5 bg-white border border-gray-200 rounded-full p-0.5 shadow-sm touch-manipulation"
                    >
                      <X size={10} className="text-gray-500" />
                    </button>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm sm:text-base font-semibold text-gray-900 truncate" title={item.name}>
                    {item.name}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {extractPriceHeading(item) || "per unit"}
                  </p>
                </div>
                <span
                  className={`text-blue-600 font-bold text-sm sm:text-base flex-shrink-0 min-w-[4.5rem] text-right ${
                    itemHasVariations(item) ? "max-w-[9rem] sm:max-w-[14rem]" : ""
                  }`}
                >
                  {itemHasVariations(item)
                    ? formatItemCanvasPrice(item, formatPrice)
                    : formatPrice(item.price)}
                </span>
                <button
                  onClick={() => handleToggleItemFavourite(item.id, !item.isFavourite)}
                  aria-pressed={!!item.isFavourite}
                  aria-label={item.isFavourite ? `Remove ${item.name} from favourites` : `Add ${item.name} to favourites`}
                  title={item.isFavourite ? "Remove from favourites" : "Add to favourites"}
                  className="flex-shrink-0 rounded-lg p-1.5 touch-manipulation hover:bg-gray-50"
                >
                  <Heart
                    size={16}
                    className={item.isFavourite ? "fill-rose-500 text-rose-500" : "text-gray-300"}
                  />
                </button>
              </div>

              <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-50">
                <span className="text-xs text-gray-400 mr-auto">
                  {item.isVisible !== false ? "Visible" : "Hidden"}
                </span>
                <button
                  onClick={() =>
                    handleToggleItemVisibility(item.id, item.isVisible === false)
                  }
                  role="switch"
                  aria-checked={item.isVisible !== false}
                  aria-label="Toggle visibility"
                  className={`relative inline-flex items-center w-11 h-6 rounded-full border-2 transition-all duration-200 focus:outline-none flex-shrink-0 ${
                    item.isVisible !== false
                      ? "bg-blue-600 border-blue-600"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      item.isVisible !== false
                        ? "translate-x-5"
                        : "translate-x-0.5 bg-gray-400"
                    }`}
                  />
                </button>
                <button
                  onClick={() => handleGenerateItemImage(item)}
                  disabled={generatingImageForId === item.id || enhancingImageForId === item.id}
                  className="border border-gray-200 p-2 rounded-lg touch-manipulation disabled:opacity-50"
                  aria-label={item.image ? `Regenerate image for ${item.name}` : `Generate image for ${item.name}`}
                  title={item.image ? "Regenerate image" : "Generate image"}
                >
                  {generatingImageForId === item.id ? (
                    <Loader2 size={13} className="text-blue-600 animate-spin" />
                  ) : (
                    <ImagePlus size={13} className="text-gray-400" />
                  )}
                </button>
                <ItemMediaUpload
                  itemName={item.name}
                  hasMedia={!!item.image}
                  uploading={uploadingMediaForId === item.id}
                  disabled={enhancingImageForId === item.id}
                  onFile={(file) => handleUploadMedia(item, file)}
                  onError={(message) => setImageGenError({ itemId: item.id, message })}
                />
                {canEnhanceImage(item.image) && (
                  <button
                    onClick={() => handleEnhanceItemImage(item)}
                    disabled={enhancingImageForId !== null || generatingImageForId === item.id || uploadingMediaForId === item.id}
                    className="border border-gray-200 p-2 rounded-lg touch-manipulation disabled:opacity-50"
                    aria-label={`Enhance image for ${item.name}`}
                    title="Enhance with AI (clean white background, better light, sharper)"
                  >
                    {enhancingImageForId === item.id ? (
                      <Loader2 size={13} className="text-blue-600 animate-spin" />
                    ) : (
                      <Sparkles size={13} className="text-gray-400" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => openEditItem(item)}
                  className="border border-gray-200 p-2 rounded-lg touch-manipulation"
                  aria-label="Edit item"
                >
                  <Pencil size={13} className="text-gray-400" />
                </button>
                <button
                  onClick={() =>
                    setDeleteConfirm({ type: "item", id: item.id, name: item.name })
                  }
                  className="bg-red-50 p-2 rounded-lg touch-manipulation"
                  aria-label="Delete item"
                >
                  <Trash2 size={13} className="text-red-400" />
                </button>
              </div>

              {imageGenError?.itemId === item.id && (
                <p className="text-xs text-red-500 mt-2">{imageGenError.message}</p>
              )}

              {enhancingImageForId === item.id && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50"
                >
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent animate-shimmer motion-reduce:animate-none" />
                    <Loader2 size={16} className="relative text-blue-600 animate-spin" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-700">Enhancing image…</p>
                    <p className="text-[11px] text-gray-400">AI is retouching it — up to 30 seconds. Your saved image stays as it is until you save the result.</p>
                    <div className="relative h-1 mt-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-blue-600 animate-indeterminate motion-reduce:animate-none" />
                    </div>
                  </div>
                </div>
              )}

              {generatingImageForId === item.id && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50"
                >
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent animate-shimmer motion-reduce:animate-none" />
                    <Loader2 size={16} className="relative text-blue-600 animate-spin" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-700">Creating image…</p>
                    <p className="text-[11px] text-gray-400">This can take up to 20 seconds</p>
                    <div className="relative h-1 mt-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-blue-600 animate-indeterminate motion-reduce:animate-none" />
                    </div>
                  </div>
                </div>
              )}

              {pendingImage?.itemId === item.id && (
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50">
                  <button
                    type="button"
                    onClick={() =>
                      setPreviewImage({
                        itemId: item.id,
                        itemName: item.name,
                        url: pendingImage.url,
                        isPending: true,
                        enhanced: !!pendingImage.enhanced,
                      })
                    }
                    aria-label={`View ${pendingImage.enhanced ? "enhanced" : "generated"} preview for ${item.name}`}
                    className="block flex-shrink-0 touch-manipulation"
                  >
                    <img
                      src={pendingImage.url}
                      alt={`${pendingImage.enhanced ? "Enhanced" : "Generated"} preview for ${item.name}`}
                      className="w-16 h-16 rounded-lg object-cover border border-gray-100"
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">
                      {pendingImage.enhanced ? "Enhanced image — not saved yet. Saving replaces the current image." : "Generated image — not saved yet."}
                    </p>
                    {pendingImage.enhanced && pendingImage.fallback && (
                      <p className="text-[11px] text-amber-700 mt-0.5">
                        AI enhancement wasn't available, so the standard enhance was used.
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <button
                        onClick={() => handleSaveGeneratedImage(item.id)}
                        className="flex items-center gap-1 bg-blue-600 text-white text-xs font-semibold px-2.5 py-1 rounded-lg touch-manipulation"
                      >
                        <Save size={12} /> Save
                      </button>
                      <button
                        onClick={handleDiscardGeneratedImage}
                        className="flex items-center gap-1 border border-gray-200 text-gray-500 text-xs font-semibold px-2.5 py-1 rounded-lg touch-manipulation"
                      >
                        <X size={12} /> Discard
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Image preview pop-up -- opened by clicking a saved or pending item image */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative w-full max-w-sm bg-white rounded-2xl overflow-hidden shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewImage(null)}
              aria-label="Close image preview"
              className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow touch-manipulation"
            >
              <X size={16} className="text-gray-600" />
            </button>
            {isVideoUrl(previewImage.url) ? (
              <video
                src={previewImage.url}
                controls
                autoPlay
                playsInline
                className="w-full aspect-square object-contain bg-black"
              />
            ) : (
              <img
                src={previewImage.url}
                alt={previewImage.itemName}
                className="w-full aspect-square object-contain bg-gray-50"
              />
            )}
            <div className="p-3 border-t border-gray-100">
              <p className="text-sm font-semibold text-gray-900 truncate mb-2">
                {previewImage.itemName}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {!previewImage.enhanced && (
                  <button
                    onClick={handleRegenerateFromPreview}
                    className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg touch-manipulation"
                  >
                    <RefreshCw size={13} /> Regenerate
                  </button>
                )}
                {!previewImage.isPending && canEnhanceImage(previewImage.url) && (
                  <button
                    onClick={() => {
                      const item = filteredItems.find((i) => i.id === previewImage.itemId) || {
                        id: previewImage.itemId,
                        name: previewImage.itemName,
                      };
                      setPreviewImage(null);
                      handleEnhanceItemImage(item);
                    }}
                    disabled={enhancingImageForId !== null}
                    className="flex items-center gap-1.5 border border-gray-200 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-lg touch-manipulation disabled:opacity-50"
                  >
                    <Sparkles size={13} /> Enhance
                  </button>
                )}
                {previewImage.isPending ? (
                  <>
                    <button
                      onClick={() => {
                        handleSaveGeneratedImage(previewImage.itemId);
                        setPreviewImage(null);
                      }}
                      className="flex items-center gap-1.5 border border-gray-200 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-lg touch-manipulation"
                    >
                      <Save size={13} /> Save
                    </button>
                    <button
                      onClick={() => {
                        handleDiscardGeneratedImage();
                        setPreviewImage(null);
                      }}
                      className="flex items-center gap-1.5 border border-gray-200 text-gray-500 text-xs font-semibold px-3 py-1.5 rounded-lg touch-manipulation"
                    >
                      <X size={13} /> Discard
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      handleRemoveSavedImage(previewImage.itemId, () => setPreviewImage(null));
                    }}
                    className="flex items-center gap-1.5 border border-gray-200 text-gray-500 text-xs font-semibold px-3 py-1.5 rounded-lg touch-manipulation"
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAB — mobile only */}
      <button
        onClick={openAddItem}
        className="sm:hidden fixed bottom-[5.5rem] right-4 bg-blue-600 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-30 touch-manipulation"
        aria-label="Add item"
      >
        <Plus size={22} className="text-white" />
      </button>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-[100dvh] bg-gray-100 w-full min-w-0 overflow-x-hidden flex flex-col">
      <StoreSwitcher />
      <div className="flex-1 pb-28 sm:pb-32 w-full min-w-0 overflow-x-hidden">
        {view === "sections" ? <SectionsView /> : <ItemsView />}
      </div>

      <BottomTabBar />

        {/* ── Add / Update Item Modal ─────────────────────────────────── */}
        {isItemModalOpen && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-8">
            <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl px-4 sm:px-5 py-4 sm:py-5 shadow-2xl max-h-[92dvh] overflow-y-auto">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingItem ? "Update Item" : "Add Item"}
                </h2>
                <button onClick={() => setIsItemModalOpen(false)}>
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 font-medium mb-1 block">Item Name</label>
                  <input
                    className={inputCls}
                    placeholder="Enter the name"
                    value={itemFormData.name}
                    onChange={(e) => setItemFormData((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                {itemFormData.variations.length === 0 ? (
                  <>
                    <div>
                      <label className="text-sm text-gray-600 font-medium mb-1 block">Price (optional)</label>
                      <input
                        className={inputCls}
                        placeholder="Enter the price"
                        value={itemFormData.price}
                        onChange={(e) => setItemFormData((p) => ({ ...p, price: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 font-medium mb-1 block">
                        Price Heading (optional)
                      </label>
                      <input
                        className={inputCls}
                        placeholder="e.g. On Roll, Per Lb"
                        value={itemFormData.priceHeading}
                        onChange={(e) => setItemFormData((p) => ({ ...p, priceHeading: e.target.value }))}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setVariationErrors([]);
                        setItemFormData((p) => ({
                          ...p,
                          variations: (() => {
                            const fromLegacy = rowsFromLegacyPrice(p.price, p.priceHeading);
                            return fromLegacy.length
                              ? fromLegacy
                              : [
                                  { label: p.priceHeading || "", price: p.price || "" },
                                  { label: "", price: "" },
                                ];
                          })(),
                        }));
                      }}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600"
                    >
                      <Plus size={14} /> Add variations (Roll, Wedge, Hero…)
                    </button>
                  </>
                ) : (
                  <div>
                    <label className="text-sm text-gray-600 font-medium mb-1 block">Variations</label>
                    <div className="space-y-2">
                      {itemFormData.variations.map((row, index) => {
                        const labelErr = variationErrors.find((e) => e.index === index && e.field === "label");
                        const priceErr = variationErrors.find((e) => e.index === index && e.field === "price");
                        return (
                          <div key={index}>
                            <div className="flex items-center gap-2">
                              <div className="min-w-0 flex-1">
                              <input
                                className={`${inputCls} !px-3`}
                                placeholder="Name (e.g. Roll)"
                                aria-label={`Variation ${index + 1} name`}
                                aria-invalid={!!labelErr}
                                value={row.label}
                                onChange={(e) => {
                                  const label = e.target.value;
                                  setItemFormData((p) => ({
                                    ...p,
                                    variations: p.variations.map((r, i) => (i === index ? { ...r, label } : r)),
                                  }));
                                }}
                              />
                              </div>
                              <div className="w-24 flex-none">
                              <input
                                className={`${inputCls} !px-3`}
                                placeholder="Price"
                                inputMode="decimal"
                                aria-label={`Variation ${index + 1} price`}
                                aria-invalid={!!priceErr}
                                value={row.price}
                                onChange={(e) => {
                                  const price = e.target.value;
                                  setItemFormData((p) => ({
                                    ...p,
                                    variations: p.variations.map((r, i) => (i === index ? { ...r, price } : r)),
                                  }));
                                }}
                              />
                              </div>
                              <button
                                type="button"
                                aria-label={`Remove variation ${row.label || index + 1}`}
                                onClick={() => {
                                  setVariationErrors([]);
                                  setItemFormData((p) => {
                                    const remaining = p.variations.filter((_, i) => i !== index);
                                    // Back to the plain editor: drop the stale combined price/heading.
                                    return remaining.length === 0
                                      ? { ...p, variations: [], price: "", priceHeading: "" }
                                      : { ...p, variations: remaining };
                                  });
                                }}
                                className="flex-none rounded-lg p-2 text-gray-400 hover:bg-gray-50 hover:text-red-600"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                            {(labelErr || priceErr) && (
                              <p className="mt-1 text-xs text-red-600" role="alert">
                                {[labelErr?.message, priceErr?.message].filter(Boolean).join(" ")}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Each variation needs a name and a price.
                    </p>
                    {itemFormData.variations.length === 1 && (
                      <p className="mt-2 text-xs text-gray-500">
                        With only one variation this item is saved as a normal single price.
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setItemFormData((p) => ({ ...p, variations: [...p.variations, { label: "", price: "" }] }))
                        }
                        className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600"
                      >
                        <Plus size={14} /> Add Variation
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setVariationErrors([]);
                          // Back to one plain price (keeps the first option's price).
                          setItemFormData((p) => ({
                            ...p,
                            variations: [],
                            price: p.variations[0]?.price || "",
                            priceHeading: "",
                          }));
                        }}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-red-600"
                      >
                        <Trash2 size={14} /> Remove all variations
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-600 font-medium mb-1 block">
                    Description (optional)
                  </label>
                  <textarea
                    className={`${inputCls} resize-none`}
                    placeholder="Short description..."
                    rows={2}
                    value={itemFormData.description}
                    onChange={(e) => setItemFormData((p) => ({ ...p, description: e.target.value }))}
                  />
                </div>
              </div>
              <button
                onClick={handleItemSubmitLocal}
                className="w-full bg-blue-600 text-white font-bold text-base py-4 rounded-full mt-6"
              >
                {editingItem ? "Update" : "Add Item"}
              </button>
            </div>
          </div>
        )}

        {/* ── Section Create / Edit Modal ─────────────────────────────── */}
        {isSectionModalOpen && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-8">
            <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl px-4 sm:px-5 py-4 sm:py-5 shadow-2xl">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingSectionId ? "Edit Section" : "Add Section"}
                </h2>
                <button
                  onClick={() => {
                    setIsSectionModalOpen(false);
                    setEditingSectionId(null);
                  }}
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium mb-1 block">
                  Section Name
                </label>
                <input
                  className={inputCls}
                  placeholder="e.g. Fish, Meat, Grocery"
                  value={sectionNameInput}
                  onChange={(e) => setSectionNameInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSectionSubmitLocal()}
                  autoFocus
                />
              </div>
              <button
                onClick={handleSectionSubmitLocal}
                className="w-full bg-blue-600 text-white font-bold text-base py-4 rounded-full mt-6"
              >
                {editingSectionId ? "Update Section" : "Add Section"}
              </button>
            </div>
          </div>
        )}


        {/* ── Data Preview Modal ──────────────────────────────────────── */}
        {isJsonPreviewOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-8">
            <div className="bg-white w-full sm:max-w-xl rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[85vh]">
              <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <h2 className="text-xl font-bold text-gray-900">Data Preview</h2>
                <button onClick={() => setIsJsonPreviewOpen(false)}>
                  <X size={20} className="text-gray-400" />
                </button>
              </div>
              <div className="flex border-b border-gray-100 px-5 flex-shrink-0">
                {["json", "csv"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setPreviewMode(tab)}
                    className={`py-3 mr-5 text-sm font-medium border-b-2 -mb-px uppercase ${
                      previewMode === tab
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-gray-400"
                    }`}
                  >
                    {tab === "csv" ? "Data CSV" : "JSON"}
                  </button>
                ))}
              </div>
              <div className="p-5 overflow-auto flex-1">
                {previewMode === "json" ? (
                  <pre className="bg-gray-100 rounded-xl p-4 text-xs text-gray-700 overflow-auto max-h-72">
                    {previewJson}
                  </pre>
                ) : (
                  <textarea
                    readOnly
                    value={previewCsv}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-xs font-mono text-gray-700 resize-none outline-none h-64"
                  />
                )}
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
                <button
                  onClick={() => setIsJsonPreviewOpen(false)}
                  className="w-full border border-gray-300 text-gray-700 font-semibold py-3 rounded-full text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Confirmation ─────────────────────────────────────── */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4 sm:p-8">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl p-5 sm:p-6 w-full max-w-sm shadow-2xl">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Delete {deleteConfirm.type === "item" ? "item" : "section"}
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete{" "}
                <strong className="text-gray-900 break-all">"{deleteConfirm.name}"</strong>?
                This cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-full text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-500 text-white font-bold py-3 rounded-full text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Loading overlay ─────────────────────────────────────────── */}
        {isLoading && (
          <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl px-6 py-4 shadow-2xl flex items-center gap-3">
              <RefreshCw size={18} className="animate-spin text-blue-600" />
              <span className="text-sm font-medium text-gray-700">
                Loading Menu from Cloud...
              </span>
            </div>
          </div>
        )}
    </div>
  );
};

export default AIMenuScreen;
